/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useCallback } from "react";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import {
  PrimaryButton,
  SecondaryButton,
  FormColorPicker,
  FormUpload,
  TagInput,
} from "../../components";
import {
  useHowItWorks,
  IHowItWorksTab,
  IWhatToDropItem,
  ILearnMoreSection,
} from "../../store/useHowItWorks";
import { useMarketPricing } from "../../store/useMarketPricing";
import { useUploadImage } from "../../store/useUploadImage";
import { useAlert } from "../../store/useAlert";
import HowItWorksPreview from "./HowItWorksPreview";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Sortable row wrapper — wraps one material item with a drag handle
// ---------------------------------------------------------------------------

function SortableItemRow({
  id,
  children,
}: {
  id: string;
  children: (
    dragHandleProps: React.HTMLAttributes<HTMLDivElement>
  ) => React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ ...attributes, ...listeners })}
    </div>
  );
}

type TabType = "how-it-works" | "what-to-drop" | "learn-more";
const TAB_LABELS: Record<TabType, string> = {
  "how-it-works": "How it works",
  "what-to-drop": "What to drop",
  "learn-more": "Learn more",
};

function emptyItem(): Omit<IWhatToDropItem, "_id" | "createdAt" | "updatedAt"> {
  return {
    key: "",
    en_title: "",
    nl_title: "",
    containerColor: "#F6EAE2",
    panelColor: "#FF6E41",
    panelTextColor: "",
    accentColor: "#FF6E41",
    itemsImageBgColor: "#FFFFFF33",
    en_price: "",
    nl_price: "",
    en_dos: [],
    nl_dos: [],
    en_donts: [],
    nl_donts: [],
    en_howText: [],
    nl_howText: [],
    en_whatHappensText: "",
    nl_whatHappensText: "",
    itemsImage: null,
    methodImage: null,
    whatHappensImage: null,
    videoUrl: null,
    linkedMaterialType: null,
    order: 0,
    isEnabled: true,
  };
}

// ---------------------------------------------------------------------------
// Sub-component: Tab editor (How it works / Learn more)
// ---------------------------------------------------------------------------

function TabEditor({
  tab,
  onSave,
  onChange,
  isSaving,
}: {
  tab: IHowItWorksTab;
  onSave: (updates: Partial<IHowItWorksTab>) => void;
  onChange?: (draft: IHowItWorksTab) => void;
  isSaving: boolean;
}) {
  const [draft, setDraft] = useState<IHowItWorksTab>({ ...tab });

  // Sync when tab changes (e.g. switching tabs)
  useEffect(() => {
    setDraft({ ...tab });
  }, [tab]);

  const set = <K extends keyof IHowItWorksTab>(
    key: K,
    val: IHowItWorksTab[K]
  ) =>
    setDraft((d) => {
      const next = { ...d, [key]: val };
      onChange?.(next);
      return next;
    });

  const isHowItWorks = tab.type === "how-it-works";
  const isLearnMore = tab.type === "learn-more";
  const isWhatToDrop = tab.type === "what-to-drop";

  return (
    <div className="space-y-6">
      {/* Top title (learn-more) — above the description */}
      {isLearnMore && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Top title (EN) — optional
            </label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-droppie-petrol/50"
              value={draft.en_topTitle ?? ""}
              onChange={(e) => set("en_topTitle", e.target.value)}
              placeholder="Optional heading shown at the top"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Top title (NL) — optional
            </label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-droppie-petrol/50"
              value={draft.nl_topTitle ?? ""}
              onChange={(e) => set("nl_topTitle", e.target.value)}
              placeholder="Optionele titel bovenaan"
            />
          </div>
        </div>
      )}

      {/* Description */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="flex justify-between mb-1">
            <label className="text-sm font-semibold text-gray-700">
              Description (EN)
            </label>
            <span className="text-xs text-gray-400">
              {draft.en_description.length} chars
            </span>
          </div>
          <textarea
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-droppie-petrol/50"
            value={draft.en_description}
            onChange={(e) => set("en_description", e.target.value)}
          />
        </div>
        <div>
          <div className="flex justify-between mb-1">
            <label className="text-sm font-semibold text-gray-700">
              Description (NL)
            </label>
            <span className="text-xs text-gray-400">
              {draft.nl_description.length} chars
            </span>
          </div>
          <textarea
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-droppie-petrol/50"
            value={draft.nl_description}
            onChange={(e) => set("nl_description", e.target.value)}
          />
        </div>
      </div>

      {/* Section color */}
      <div>
        <FormColorPicker
          label="Section background color"
          value={draft.sectionColor}
          onChange={(c) => set("sectionColor", c)}
        />
      </div>

      {/* Steps (how-it-works) */}
      {isHowItWorks && (
        <>
          <StepsEditor
            label="Steps (EN)"
            steps={draft.en_steps}
            onChange={(s) => set("en_steps", s)}
          />
          <StepsEditor
            label="Steps (NL)"
            steps={draft.nl_steps}
            onChange={(s) => set("nl_steps", s)}
          />
        </>
      )}

      {/* Legend bullets (what-to-drop) */}
      {isWhatToDrop && (
        <>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Legend bullets (EN)
            </label>
            <TagInput
              keywords={draft.en_details}
              setKeywords={(kw) =>
                set(
                  "en_details",
                  typeof kw === "function" ? kw(draft.en_details) : kw
                )
              }
              placeholder="Type and press Enter"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Legend bullets (NL)
            </label>
            <TagInput
              keywords={draft.nl_details}
              setKeywords={(kw) =>
                set(
                  "nl_details",
                  typeof kw === "function" ? kw(draft.nl_details) : kw
                )
              }
              placeholder="Type and press Enter"
            />
          </div>
        </>
      )}

      {/* Titled bullet sections (learn-more) */}
      {isLearnMore && (
        <>
          <LearnMoreSectionsEditor
            sections={draft.learnMoreSections ?? []}
            onChange={(s) => set("learnMoreSections", s)}
          />
          <VideoUrlsEditor
            videos={draft.videos ?? []}
            onChange={(v) => set("videos", v)}
          />
        </>
      )}

      <div className="flex justify-end">
        <PrimaryButton
          label={isSaving ? "Saving…" : "Save tab"}
          onClick={() => onSave(draft)}
          disabled={isSaving}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: Steps editor (for how-it-works)
// ---------------------------------------------------------------------------

function StepsEditor({
  label,
  steps,
  onChange,
}: {
  label: string;
  steps: string[];
  onChange: (steps: string[]) => void;
}) {
  const update = (i: number, val: string) => {
    const next = [...steps];
    next[i] = val;
    onChange(next);
  };
  const add = () => onChange([...steps, ""]);
  const remove = (i: number) => onChange(steps.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-semibold text-gray-700">{label}</label>
        <button
          type="button"
          onClick={add}
          className="text-xs text-droppie-petrol hover:text-droppie-petrol-dark font-semibold"
        >
          + Add step
        </button>
      </div>
      <div className="space-y-2">
        {steps.map((step, i) => (
          <div key={i} className="flex gap-2 items-center">
            <span className="text-xs font-bold text-gray-400 w-14 shrink-0">
              Step {i + 1}
            </span>
            <input
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-droppie-petrol/50"
              value={step}
              onChange={(e) => update(i, e.target.value)}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-red-400 hover:text-red-600 text-sm font-bold px-1"
            >
              ✕
            </button>
          </div>
        ))}
        {steps.length === 0 && (
          <p className="text-xs text-gray-400">
            No steps yet. Click "Add step".
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: Video URLs editor (for learn-more tab)
// ---------------------------------------------------------------------------

function VideoUrlsEditor({
  videos,
  onChange,
}: {
  videos: string[];
  onChange: (videos: string[]) => void;
}) {
  const update = (i: number, val: string) => {
    const next = [...videos];
    next[i] = val;
    onChange(next);
  };
  const add = () => onChange([...videos, ""]);
  const remove = (i: number) => onChange(videos.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-semibold text-gray-700">
          YouTube videos
        </label>
        <button
          type="button"
          onClick={add}
          className="text-xs text-droppie-petrol hover:text-droppie-petrol-dark font-semibold"
        >
          + Add video
        </button>
      </div>
      <div className="space-y-2">
        {videos.map((url, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-droppie-petrol/50"
              value={url}
              onChange={(e) => update(i, e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-red-400 hover:text-red-600 text-sm font-bold px-1"
            >
              ✕
            </button>
          </div>
        ))}
        {videos.length === 0 && (
          <p className="text-xs text-gray-400">
            No videos yet. Click "Add video".
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: Titled bullet sections editor (Learn more)
// ---------------------------------------------------------------------------

function LearnMoreSectionsEditor({
  sections,
  onChange,
}: {
  sections: ILearnMoreSection[];
  onChange: (sections: ILearnMoreSection[]) => void;
}) {
  const update = (i: number, patch: Partial<ILearnMoreSection>) =>
    onChange(sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const add = () =>
    onChange([
      ...sections,
      { en_title: "", nl_title: "", en_bullets: [], nl_bullets: [] },
    ]);
  const remove = (i: number) =>
    onChange(sections.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-semibold text-gray-700">
          Sections (title + bullets)
        </label>
        <button
          type="button"
          onClick={add}
          className="text-xs text-droppie-petrol hover:text-droppie-petrol-dark font-semibold"
        >
          + Add section
        </button>
      </div>
      <div className="space-y-4">
        {sections.map((sec, i) => (
          <div
            key={i}
            className="border border-gray-200 rounded-lg p-3 space-y-3"
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-500">
                Section {i + 1}
              </p>
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-red-400 hover:text-red-600 text-sm font-bold px-1"
              >
                Remove
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Title (EN)
                </label>
                <input
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-droppie-petrol/50"
                  value={sec.en_title}
                  onChange={(e) => update(i, { en_title: e.target.value })}
                  placeholder="Section title"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Title (NL)
                </label>
                <input
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-droppie-petrol/50"
                  value={sec.nl_title}
                  onChange={(e) => update(i, { nl_title: e.target.value })}
                  placeholder="Sectietitel"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Bullets (EN)
              </label>
              <TagInput
                keywords={sec.en_bullets}
                setKeywords={(kw) =>
                  update(i, {
                    en_bullets:
                      typeof kw === "function" ? kw(sec.en_bullets) : kw,
                  })
                }
                placeholder="Type and press Enter"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Bullets (NL)
              </label>
              <TagInput
                keywords={sec.nl_bullets}
                setKeywords={(kw) =>
                  update(i, {
                    nl_bullets:
                      typeof kw === "function" ? kw(sec.nl_bullets) : kw,
                  })
                }
                placeholder="Type and press Enter"
              />
            </div>
          </div>
        ))}
        {sections.length === 0 && (
          <p className="text-xs text-gray-400">
            No sections yet. Click "Add section".
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: Material item editor (What to drop)
// ---------------------------------------------------------------------------

function ItemEditor({
  item,
  onSave,
  onDelete,
  onCancel,
  onChange,
  isSaving,
  isNew,
}: {
  item:
    | IWhatToDropItem
    | Omit<IWhatToDropItem, "_id" | "createdAt" | "updatedAt">;
  onSave: (updates: typeof item) => void;
  onDelete?: () => void;
  onCancel: () => void;
  onChange?: (draft: typeof item) => void;
  isSaving: boolean;
  isNew?: boolean;
}) {
  const { uploadImage, uploadedImage, clearUploadedImage } = useUploadImage();
  const { marketPrices, getAllMarketPrices } = useMarketPricing();
  const [draft, setDraft] = useState<typeof item>({ ...item });
  const [lastUploadTarget, setLastUploadTarget] = useState<
    "itemsImage" | "methodImage" | "whatHappensImage" | null
  >(null);
  const [itemsImageFile, setItemsImageFile] = useState<File | null>(null);
  const [methodImageFile, setMethodImageFile] = useState<File | null>(null);
  const [whatHappensImageFile, setWhatHappensImageFile] = useState<File | null>(
    null
  );

  useEffect(() => {
    if (!marketPrices?.length) getAllMarketPrices();
  }, []);

  useEffect(() => {
    setDraft({ ...item });
  }, []);

  useEffect(() => {
    if (uploadedImage && lastUploadTarget) {
      setDraft((d) => {
        const next = { ...d, [lastUploadTarget]: uploadedImage };
        onChange?.(next);
        return next;
      });
      clearUploadedImage();
      setLastUploadTarget(null);
    }
  }, [uploadedImage]);

  const set = <K extends keyof typeof draft>(key: K, val: (typeof draft)[K]) =>
    setDraft((d) => {
      const next = { ...d, [key]: val };
      onChange?.(next);
      return next;
    });

  const setArr = (
    key: keyof typeof draft,
    kw: string[] | ((prev: string[]) => string[])
  ) =>
    setDraft((d) => {
      const nextVal = typeof kw === "function" ? kw(d[key] as string[]) : kw;
      const next = { ...d, [key]: nextVal };
      onChange?.(next);
      return next;
    });

  const formatPrice = (rate: number, measurement: string) => {
    const unit =
      measurement === "kg"
        ? "KG"
        : measurement === "unit"
          ? "item"
          : measurement;
    return `€${rate}/${unit}`;
  };

  const applyMaterial = (materialType: string | null) => {
    if (!materialType) {
      setDraft((d) => {
        const next = { ...d, linkedMaterialType: null };
        onChange?.(next);
        return next;
      });
      return;
    }
    const mat = (marketPrices ?? []).find((m) => m.type === materialType);
    if (!mat) return;
    setDraft((d) => {
      const next: typeof d = {
        ...d,
        linkedMaterialType: materialType,
        ...(mat.primaryColor
          ? {
              containerColor: mat.primaryColor,
              itemsImageBgColor: mat.primaryColor,
            }
          : {}),
        ...(mat.secondaryColor
          ? { panelColor: mat.secondaryColor, accentColor: mat.secondaryColor }
          : {}),
        en_price: formatPrice(mat.rate, mat.measurement),
        nl_price: formatPrice(mat.rate, mat.measurement),
      };
      onChange?.(next);
      return next;
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm space-y-5">
      {/* Link to material */}
      <div className="bg-droppie-lichtblauw/20 border border-droppie-lichtblauw rounded-xl p-3">
        <label className="block text-xs font-semibold text-droppie-petrol mb-1">
          Link to material (auto-fills color & price)
        </label>
        <div className="flex gap-2 items-center">
          <select
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-droppie-petrol/50 bg-white"
            value={draft.linkedMaterialType ?? ""}
            onChange={(e) => applyMaterial(e.target.value || null)}
          >
            <option value="">— Manual (no link) —</option>
            {(marketPrices ?? [])
              .filter((m) => m.isActive !== false)
              .map((m) => (
                <option key={m.type} value={m.type}>
                  {m.name} (
                  {m.measurement === "kg"
                    ? "€" + m.rate + "/KG"
                    : "€" + m.rate + "/" + m.measurement}
                  ){m.primaryColor ? " 🎨" : ""}
                </option>
              ))}
          </select>
        </div>
        {draft.linkedMaterialType && (
          <p className="text-xs text-droppie-petrol/70 mt-1">
            Panel & accent color + price synced from material. You can still
            override below.
          </p>
        )}
      </div>

      {/* Key + titles */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Key (unique slug)
          </label>
          <input
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-droppie-petrol/50"
            value={draft.key}
            onChange={(e) => set("key", e.target.value)}
            placeholder="e.g. Plastic"
            disabled={!isNew}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Title (EN)
          </label>
          <input
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-droppie-petrol/50"
            value={draft.en_title}
            onChange={(e) => set("en_title", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Title (NL)
          </label>
          <input
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-droppie-petrol/50"
            value={draft.nl_title}
            onChange={(e) => set("nl_title", e.target.value)}
          />
        </div>
      </div>

      {/* Prices */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Price (EN)
          </label>
          <input
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-droppie-petrol/50"
            value={draft.en_price}
            placeholder="e.g. €0.20/KG"
            onChange={(e) => set("en_price", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Price (NL)
          </label>
          <input
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-droppie-petrol/50"
            value={draft.nl_price}
            placeholder="e.g. €0,20/KG"
            onChange={(e) => set("nl_price", e.target.value)}
          />
        </div>
      </div>

      {/* Colors */}
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-3">Colors</p>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <FormColorPicker
            label="Container bg"
            value={draft.containerColor}
            onChange={(c) => set("containerColor", c)}
          />
          <FormColorPicker
            label="Panel (DOs/DON'Ts bg)"
            value={draft.panelColor}
            onChange={(c) => set("panelColor", c)}
          />
          <FormColorPicker
            label="Panel text (DOs/DON'Ts)"
            value={draft.panelTextColor || ""}
            onChange={(c) => set("panelTextColor", c)}
          />
          <FormColorPicker
            label="Accent (section titles)"
            value={draft.accentColor}
            onChange={(c) => set("accentColor", c)}
          />
          <div>
            <p className="font-satoshi text-dark text-sm font-medium mb-1">
              Icons card bg
            </p>
            <p className="text-xs text-gray-400 mb-2">
              Recommended:{" "}
              <span className="font-mono font-semibold text-gray-600">
                #FFFFFF33
              </span>{" "}
              — white at 20% opacity
            </p>
            <div className="flex items-center gap-2">
              <span
                className="w-6 h-6 rounded border border-gray-300 shrink-0"
                style={{ backgroundColor: draft.itemsImageBgColor }}
              />
              <input
                className="w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-droppie-petrol/50"
                value={draft.itemsImageBgColor}
                onChange={(e) => set("itemsImageBgColor", e.target.value)}
                placeholder="#FFFFFF33"
                maxLength={9}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Images */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Items icons image (strip)
          </label>
          <FormUpload
            label="Upload"
            value={itemsImageFile}
            imageUrl={draft.itemsImage ?? undefined}
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setItemsImageFile(f);
              if (f) {
                setLastUploadTarget("itemsImage");
                uploadImage(f);
              }
            }}
            onClear={() => {
              set("itemsImage", null);
              setItemsImageFile(null);
            }}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Method illustration (How section)
          </label>
          <FormUpload
            label="Upload"
            value={methodImageFile}
            imageUrl={draft.methodImage ?? undefined}
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setMethodImageFile(f);
              if (f) {
                setLastUploadTarget("methodImage");
                uploadImage(f);
              }
            }}
            onClear={() => {
              set("methodImage", null);
              setMethodImageFile(null);
            }}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Partner logo (What happens section)
          </label>
          <FormUpload
            label="Upload"
            value={whatHappensImageFile}
            imageUrl={draft.whatHappensImage ?? undefined}
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setWhatHappensImageFile(f);
              if (f) {
                setLastUploadTarget("whatHappensImage");
                uploadImage(f);
              }
            }}
            onClear={() => {
              set("whatHappensImage", null);
              setWhatHappensImageFile(null);
            }}
          />
        </div>
      </div>

      {/* DOs */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            DOs (EN)
          </label>
          <TagInput
            keywords={draft.en_dos}
            setKeywords={(kw) => setArr("en_dos", kw)}
            placeholder="Type and press Enter"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Welles (NL)
          </label>
          <TagInput
            keywords={draft.nl_dos}
            setKeywords={(kw) => setArr("nl_dos", kw)}
            placeholder="Type and press Enter"
          />
        </div>
      </div>

      {/* DON'Ts */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            DON'Ts (EN)
          </label>
          <TagInput
            keywords={draft.en_donts}
            setKeywords={(kw) => setArr("en_donts", kw)}
            placeholder="Type and press Enter"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Nietes (NL)
          </label>
          <TagInput
            keywords={draft.nl_donts}
            setKeywords={(kw) => setArr("nl_donts", kw)}
            placeholder="Type and press Enter"
          />
        </div>
      </div>

      {/* How to drop (bullet points) */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            How (EN)
          </label>
          <TagInput
            keywords={draft.en_howText}
            setKeywords={(kw) => setArr("en_howText", kw)}
            placeholder="Type and press Enter"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Hoe? (NL)
          </label>
          <TagInput
            keywords={draft.nl_howText}
            setKeywords={(kw) => setArr("nl_howText", kw)}
            placeholder="Type and press Enter"
          />
        </div>
      </div>

      {/* What happens */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="flex justify-between mb-1">
            <label className="text-xs font-semibold text-gray-600">
              What happens text (EN)
            </label>
            <span className="text-xs text-gray-400">
              {draft.en_whatHappensText.length} chars
            </span>
          </div>
          <textarea
            rows={2}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-droppie-petrol/50"
            value={draft.en_whatHappensText}
            onChange={(e) => set("en_whatHappensText", e.target.value)}
          />
        </div>
        <div>
          <div className="flex justify-between mb-1">
            <label className="text-xs font-semibold text-gray-600">
              Wat gebeurt ermee? (NL)
            </label>
            <span className="text-xs text-gray-400">
              {draft.nl_whatHappensText.length} chars
            </span>
          </div>
          <textarea
            rows={2}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-droppie-petrol/50"
            value={draft.nl_whatHappensText}
            onChange={(e) => set("nl_whatHappensText", e.target.value)}
          />
        </div>
      </div>

      {/* Video URL */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">
          YouTube video URL (optional)
        </label>
        <input
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-droppie-petrol/50"
          value={draft.videoUrl ?? ""}
          onChange={(e) => set("videoUrl", e.target.value || null)}
          placeholder="https://www.youtube.com/watch?v=..."
        />
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center pt-2 border-t border-gray-100">
        <div className="flex gap-3 items-center">
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="text-sm text-red-500 hover:text-red-700 font-semibold"
            >
              Delete
            </button>
          )}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={draft.isEnabled}
              onChange={(e) => set("isEnabled", e.target.checked)}
              className="w-4 h-4 accent-droppie-petrol"
            />
            <span className="text-sm text-gray-600">Enabled in app</span>
          </label>
        </div>
        <div className="flex gap-2">
          <SecondaryButton label="Cancel" onClick={onCancel} />
          <PrimaryButton
            label={isSaving ? "Saving…" : isNew ? "Create" : "Save"}
            onClick={() => onSave(draft)}
            disabled={isSaving}
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function HowItWorksPage() {
  const {
    tabs,
    items,
    isSaving,
    getTabs,
    getItems,
    updateTab,
    createItem,
    updateItem,
    deleteItem,
    reorderItems,
  } = useHowItWorks();
  const { setAlert } = useAlert();

  const sensors = useSensors(useSensor(PointerSensor));

  const [activeTabType, setActiveTabType] = useState<TabType>("how-it-works");
  // Each item can be expanded independently; use a Set of _id strings.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [previewTab, setPreviewTab] = useState<TabType>("how-it-works");
  const [previewItemKey, setPreviewItemKey] = useState<string | null>(null);
  const [lang, setLang] = useState<"en" | "nl">("en");

  // Live draft overlays — keyed by tab type / item _id.
  // These are merged over the store data so the preview updates without saving.
  const [liveTabDrafts, setLiveTabDrafts] = useState<
    Partial<Record<TabType, IHowItWorksTab>>
  >({});
  const [liveItemDrafts, setLiveItemDrafts] = useState<
    Record<string, IWhatToDropItem>
  >({});

  useEffect(() => {
    getTabs();
    getItems();
  }, []);

  const activeTab = tabs.find((t) => t.type === activeTabType);

  const previewTabs = tabs.map((t) => liveTabDrafts[t.type as TabType] ?? t);
  const previewItems = items.map((it) => liveItemDrafts[it._id!] ?? it);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleSaveTab = useCallback(
    async (updates: Partial<IHowItWorksTab>) => {
      const ok = await updateTab(activeTabType, updates);
      setAlert({
        isOpen: true,
        message: ok ? "Tab saved." : "Error saving tab.",
        severity: ok ? "success" : "error",
      });
    },
    [activeTabType]
  );

  const handleSaveItem = useCallback(
    async (
      id: string | "new",
      data:
        | IWhatToDropItem
        | Omit<IWhatToDropItem, "_id" | "createdAt" | "updatedAt">
    ) => {
      let ok: boolean;
      if (id === "new") {
        ok = await createItem(
          data as Omit<IWhatToDropItem, "_id" | "createdAt" | "updatedAt">
        );
        if (ok) setIsAddingNew(false);
      } else {
        ok = await updateItem(id, data as Partial<IWhatToDropItem>);
        if (ok) {
          setExpandedIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
      }
      setAlert({
        isOpen: true,
        message: ok ? "Item saved." : "Error saving item.",
        severity: ok ? "success" : "error",
      });
    },
    []
  );

  const handleDeleteItem = useCallback(async (id: string) => {
    if (!window.confirm("Delete this material item?")) return;
    const ok = await deleteItem(id);
    setAlert({
      isOpen: true,
      message: ok ? "Item deleted." : "Error deleting item.",
      severity: ok ? "success" : "error",
    });
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = items.findIndex((it) => it._id === active.id);
      const newIndex = items.findIndex((it) => it._id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(items, oldIndex, newIndex);
      await reorderItems(reordered.map((it) => it._id!));
    },
    [items, reorderItems]
  );

  const tabTypes: TabType[] = ["how-it-works", "what-to-drop", "learn-more"];

  return (
    <div className="p-6 max-w-screen-xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">
        How it works sheet
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        Edit the content shown in the app's "How it works" bottom sheet. Changes
        are reflected immediately after the user refreshes the app.
      </p>

      <div className="flex gap-6 flex-col xl:flex-row">
        {/* ── Left: Editor ── */}
        <div className="flex-1 min-w-0">
          {/* Tab selector */}
          <div className="flex gap-2 mb-4 border-b border-gray-200">
            {tabTypes.map((t) => (
              <button
                key={t}
                onClick={() => {
                  setActiveTabType(t);
                  setPreviewTab(t);
                  setIsAddingNew(false);
                }}
                className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${
                  activeTabType === t
                    ? "border-droppie-petrol text-droppie-petrol bg-droppie-lichtblauw/30"
                    : "border-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                }`}
              >
                {TAB_LABELS[t]}
              </button>
            ))}
          </div>

          {/* Editor area */}
          <div className="bg-white rounded-b-2xl rounded-tr-2xl border border-gray-200 shadow-sm p-6">
            {activeTabType !== "what-to-drop" && activeTab ? (
              <TabEditor
                tab={activeTab}
                onSave={handleSaveTab}
                onChange={(draft) =>
                  setLiveTabDrafts((prev) => ({
                    ...prev,
                    [activeTabType]: draft,
                  }))
                }
                isSaving={isSaving}
              />
            ) : activeTabType === "what-to-drop" ? (
              <div className="space-y-4">
                {/* What-to-drop tab meta editor */}
                {activeTab && (
                  <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 mb-4">
                    <p className="text-sm font-bold text-gray-700 mb-3">
                      Tab settings
                    </p>
                    <TabEditor
                      tab={activeTab}
                      onSave={handleSaveTab}
                      onChange={(draft) =>
                        setLiveTabDrafts((prev) => ({
                          ...prev,
                          [activeTabType]: draft,
                        }))
                      }
                      isSaving={isSaving}
                    />
                  </div>
                )}

                {/* Material items list */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-gray-800">
                      Material items ({items.length})
                    </p>
                    <span className="text-xs text-green-600 font-medium">
                      {items.filter((i) => i.isEnabled).length} enabled
                    </span>
                    {items.some((i) => !i.isEnabled) && (
                      <span className="text-xs text-gray-400 font-medium">
                        · {items.filter((i) => !i.isEnabled).length} hidden
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {expandedIds.size > 0 && (
                      <button
                        type="button"
                        onClick={() => setExpandedIds(new Set())}
                        className="text-xs text-gray-400 hover:text-gray-600 font-medium"
                      >
                        Collapse all
                      </button>
                    )}
                    <PrimaryButton
                      label="+ Add item"
                      onClick={() => {
                        setIsAddingNew(true);
                      }}
                      disabled={isAddingNew}
                    />
                  </div>
                </div>

                {/* New item form */}
                {isAddingNew && (
                  <ItemEditor
                    item={emptyItem()}
                    onSave={(data) => handleSaveItem("new", data)}
                    onCancel={() => setIsAddingNew(false)}
                    isSaving={isSaving}
                    isNew
                  />
                )}

                {/* Existing items — drag to reorder, each independently expandable */}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={items.map((it) => it._id!)}
                    strategy={verticalListSortingStrategy}
                  >
                    {items.map((it) => {
                      const isOpen = expandedIds.has(it._id!);
                      return (
                        <SortableItemRow key={it._id} id={it._id!}>
                          {(dragHandleProps) => (
                            <div className="border border-gray-200 rounded-2xl overflow-hidden">
                              {/* Always-visible header */}
                              <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 select-none">
                                {/* Drag handle */}
                                <div
                                  {...dragHandleProps}
                                  className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 shrink-0"
                                  title="Drag to reorder"
                                >
                                  ⠿
                                </div>
                                <span
                                  className="w-5 h-5 rounded-full shrink-0 border border-gray-200"
                                  style={{ backgroundColor: it.containerColor }}
                                />
                                <span
                                  className="font-semibold text-sm text-gray-800 flex-1 cursor-pointer"
                                  onClick={() => toggleExpand(it._id!)}
                                >
                                  {it.en_title || it.key}
                                </span>
                                {it.linkedMaterialType && !isOpen && (
                                  <span className="text-xs bg-droppie-lichtblauw/40 text-droppie-petrol rounded px-1.5 py-0.5 font-medium shrink-0">
                                    {it.linkedMaterialType}
                                  </span>
                                )}
                                {!it.isEnabled && (
                                  <span className="text-xs bg-gray-100 text-gray-400 rounded px-1.5 py-0.5 font-medium shrink-0">
                                    hidden
                                  </span>
                                )}
                                <span
                                  className="text-gray-400 text-sm cursor-pointer"
                                  onClick={() => toggleExpand(it._id!)}
                                >
                                  {isOpen ? "▲" : "▼"}
                                </span>
                              </div>

                              {/* Expandable editor */}
                              {isOpen && (
                                <div className="border-t border-gray-100 p-4">
                                  <ItemEditor
                                    item={it}
                                    onSave={(data) =>
                                      handleSaveItem(it._id!, data)
                                    }
                                    onDelete={() => handleDeleteItem(it._id!)}
                                    onCancel={() => toggleExpand(it._id!)}
                                    onChange={(draft) =>
                                      setLiveItemDrafts((prev) => ({
                                        ...prev,
                                        [it._id!]: draft as IWhatToDropItem,
                                      }))
                                    }
                                    isSaving={isSaving}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </SortableItemRow>
                      );
                    })}
                  </SortableContext>
                </DndContext>
              </div>
            ) : null}
          </div>
        </div>

        {/* ── Right: Live preview ── */}
        <div className="xl:w-80 shrink-0">
          <div className="sticky top-[100px]">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-bold text-gray-700">Live preview</p>
              <div className="flex gap-1">
                {(["en", "nl"] as const).map((l) => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    className={`px-2 py-0.5 text-xs rounded font-semibold ${
                      lang === l
                        ? "bg-droppie-petrol text-white"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            {/* Preview tab switcher */}
            <div className="flex gap-1 mb-3 flex-wrap">
              {tabTypes.map((t) => (
                <button
                  key={t}
                  onClick={() => {
                    setPreviewTab(t);
                    setPreviewItemKey(null);
                  }}
                  className={`px-2 py-1 text-xs rounded font-medium ${
                    previewTab === t
                      ? "bg-droppie-lichtblauw text-droppie-petrol"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {t.replace(/-/g, " ")}
                </button>
              ))}
            </div>
            <HowItWorksPreview
              tabs={previewTabs}
              items={previewItems}
              activeTabType={previewTab}
              activeItemKey={previewItemKey}
              onSelectItem={setPreviewItemKey}
              lang={lang}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(HowItWorksPage);
